/**
 * CmSourceRuleEditor — unified ordered rule list for a data source.
 * Rule types: clean | formula | lookup — all share step_order for interleaving.
 * Drag-and-drop to reorder; no manual step-order field.
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Plus, Wand2, FlaskConical, Search as SearchLucide } from 'lucide-react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import Toast, { ToastMessage } from '../common/Toast';
import FileAnalyticsAPI from '../file_analytics/fileAnalyticsService';
import { DataSource, SourceRule, SourceRuleCreate, SourceRuleUpdate, StandardColumn } from '../file_analytics/types';

// ── Clean operation catalogue ────────────────────────────────────────────────
type CleanOp =
  // Existing
  | 'fill_nulls' | 'trim_whitespace' | 'standardize_dates' | 'remove_duplicates' | 'find_replace' | 'cast_type'
  // Null-handling
  | 'fill_forward' | 'fill_backward' | 'fill_mean' | 'fill_median' | 'fill_mode' | 'flag_nulls'
  // String/text
  | 'normalize_whitespace' | 'to_uppercase' | 'to_lowercase' | 'to_titlecase'
  | 'remove_special_chars' | 'extract_regex' | 'pad_left' | 'parse_boolean' | 'standardize_number_format'
  // Dedup
  | 'flag_duplicates'
  // Range/outlier
  | 'clamp_range' | 'flag_outliers'
  // Split/combine
  | 'split_column' | 'concat_columns' | 'normalize_scale';

const CLEAN_OP_LABELS: Record<CleanOp, string> = {
  // Existing
  fill_nulls:               'Fill Null Values',
  trim_whitespace:          'Trim Whitespace',
  standardize_dates:        'Standardize Dates',
  remove_duplicates:        'Remove Duplicates',
  find_replace:             'Find & Replace',
  cast_type:                'Cast Column Type',
  // Null-handling
  fill_forward:             'Forward Fill (propagate last)',
  fill_backward:            'Backward Fill (propagate next)',
  fill_mean:                'Fill with Column Mean',
  fill_median:              'Fill with Column Median',
  fill_mode:                'Fill with Most Frequent Value',
  flag_nulls:               'Flag Nulls (new boolean column)',
  // String/text
  normalize_whitespace:     'Normalize Whitespace',
  to_uppercase:             'Convert to Uppercase',
  to_lowercase:             'Convert to Lowercase',
  to_titlecase:             'Convert to Title Case',
  remove_special_chars:     'Remove Special Characters',
  extract_regex:            'Extract via Regex',
  pad_left:                 'Left-Pad',
  parse_boolean:            'Parse Boolean',
  standardize_number_format:'Strip Currency / Commas',
  // Dedup
  flag_duplicates:          'Flag Duplicates (new boolean column)',
  // Range/outlier
  clamp_range:              'Clamp to Range',
  flag_outliers:            'Flag Outliers (new boolean column)',
  // Split/combine
  split_column:             'Split Column',
  concat_columns:           'Concatenate Columns',
  normalize_scale:          'Normalize Scale (0–1)',
};

const DEFAULT_CLEAN_PARAMS: Record<CleanOp, Record<string, string>> = {
  // Existing
  fill_nulls:               { column: '', fill_with: '0' },
  trim_whitespace:          { column: '' },
  standardize_dates:        { column: '', target_format: '%Y-%m-%d' },
  remove_duplicates:        { columns: '' },
  find_replace:             { column: '', find: '', replace: '' },
  cast_type:                { column: '', to_type: 'numeric' },
  // Null-handling
  fill_forward:             { column: '' },
  fill_backward:            { column: '' },
  fill_mean:                { column: '' },
  fill_median:              { column: '' },
  fill_mode:                { column: '' },
  flag_nulls:               { column: '' },
  // String/text
  normalize_whitespace:     { column: '' },
  to_uppercase:             { column: '' },
  to_lowercase:             { column: '' },
  to_titlecase:             { column: '' },
  remove_special_chars:     { column: '', keep_pattern: '[^a-zA-Z0-9 ]' },
  extract_regex:            { column: '', pattern: '', output_column: '' },
  pad_left:                 { column: '', width: '10', pad_char: '0' },
  parse_boolean:            { column: '' },
  standardize_number_format:{ column: '' },
  // Dedup
  flag_duplicates:          { columns: '' },
  // Range/outlier
  clamp_range:              { column: '', min_val: '', max_val: '' },
  flag_outliers:            { column: '', z_threshold: '3' },
  // Split/combine
  split_column:             { column: '', delimiter: ',', left_output: '', right_output: '' },
  concat_columns:           { col_a: '', col_b: '', separator: ' ', output_column: '' },
  normalize_scale:          { column: '' },
};

// ── Formula catalogue ────────────────────────────────────────────────────────
type FormulaGroup = 'numeric' | 'text' | 'date' | 'any';
type FormulaCategory = 'single' | 'two_col' | 'multi_col' | 'conditional' | 'case' | 'window';

interface FormulaDef {
  op: string; label: string; category: FormulaCategory;
  group: FormulaGroup[];   // which column types this op applies to (for single-col filtering)
  outputType: string;
  hasParam?: boolean;
  paramLabel?: string; paramDefault?: string; paramPlaceholder?: string;
}

const FORMULA_CATALOGUE: FormulaDef[] = [
  // ── Cat 1: Single-column transform ──────────────────────────────────────────
  // Numeric
  { op: 'ABS',              label: 'Absolute Value',       category: 'single', group: ['numeric'],       outputType: 'number' },
  { op: 'ROUND',            label: 'Round',                category: 'single', group: ['numeric'],       outputType: 'number',  hasParam: true, paramLabel: 'Decimal places', paramDefault: '2' },
  { op: 'CEIL',             label: 'Ceiling',              category: 'single', group: ['numeric'],       outputType: 'number' },
  { op: 'FLOOR',            label: 'Floor',                category: 'single', group: ['numeric'],       outputType: 'number' },
  { op: 'MOVING_AVG',       label: 'Moving Average',       category: 'single', group: ['numeric'],       outputType: 'number',  hasParam: true, paramLabel: 'Window size', paramDefault: '3', paramPlaceholder: 'e.g. 3' },
  { op: 'CUMSUM',           label: 'Cumulative Sum',       category: 'single', group: ['numeric'],       outputType: 'number' },
  { op: 'PCT_OF_TOTAL',     label: '% of Total',           category: 'single', group: ['numeric'],       outputType: 'number' },
  { op: 'Z_SCORE',          label: 'Z-Score (Standardise)',category: 'single', group: ['numeric'],       outputType: 'number' },
  { op: 'RANK',             label: 'Rank',                 category: 'single', group: ['numeric'],       outputType: 'number' },
  { op: 'LOG',              label: 'Natural Log',          category: 'single', group: ['numeric'],       outputType: 'number' },
  // Text
  { op: 'UPPER',            label: 'Uppercase',            category: 'single', group: ['text'],          outputType: 'string' },
  { op: 'LOWER',            label: 'Lowercase',            category: 'single', group: ['text'],          outputType: 'string' },
  { op: 'TRIM',             label: 'Trim Whitespace',      category: 'single', group: ['text'],          outputType: 'string' },
  { op: 'TITLE',            label: 'Title Case',           category: 'single', group: ['text'],          outputType: 'string' },
  { op: 'STR_LEN',          label: 'String Length',        category: 'single', group: ['text'],          outputType: 'number' },
  { op: 'LEFT',             label: 'First N Characters',   category: 'single', group: ['text'],          outputType: 'string', hasParam: true, paramLabel: 'Characters', paramDefault: '10' },
  { op: 'RIGHT',            label: 'Last N Characters',    category: 'single', group: ['text'],          outputType: 'string', hasParam: true, paramLabel: 'Characters', paramDefault: '10' },
  { op: 'SUBSTR',           label: 'Substring',            category: 'single', group: ['text'],          outputType: 'string', hasParam: true, paramLabel: 'start:length', paramDefault: '0:10', paramPlaceholder: 'e.g. 0:5' },
  { op: 'CONTAINS',         label: 'Contains Text',        category: 'single', group: ['text'],          outputType: 'boolean', hasParam: true, paramLabel: 'Search string', paramDefault: '' },
  { op: 'STARTS_WITH',      label: 'Starts With',          category: 'single', group: ['text'],          outputType: 'boolean', hasParam: true, paramLabel: 'Prefix', paramDefault: '' },
  { op: 'ENDS_WITH',        label: 'Ends With',            category: 'single', group: ['text'],          outputType: 'boolean', hasParam: true, paramLabel: 'Suffix', paramDefault: '' },
  { op: 'REGEX_EXTRACT',    label: 'Extract via Regex',    category: 'single', group: ['text'],          outputType: 'string', hasParam: true, paramLabel: 'Regex pattern', paramDefault: '', paramPlaceholder: 'e.g. (\\d+)' },
  // Date
  { op: 'YEAR',             label: 'Extract Year',         category: 'single', group: ['date'],          outputType: 'number' },
  { op: 'MONTH',            label: 'Extract Month',        category: 'single', group: ['date'],          outputType: 'number' },
  { op: 'QUARTER',          label: 'Extract Quarter',      category: 'single', group: ['date'],          outputType: 'number' },
  { op: 'DAY',              label: 'Extract Day',          category: 'single', group: ['date'],          outputType: 'number' },
  { op: 'DOW',              label: 'Day of Week (0=Mon)',  category: 'single', group: ['date'],          outputType: 'number' },
  { op: 'HOUR',             label: 'Extract Hour',         category: 'single', group: ['date'],          outputType: 'number' },
  { op: 'WEEKNUM',          label: 'Week Number',          category: 'single', group: ['date'],          outputType: 'number' },
  { op: 'IS_WEEKEND',       label: 'Is Weekend',           category: 'single', group: ['date'],          outputType: 'boolean' },
  { op: 'DAYS_FROM_TODAY',  label: 'Days from Today',      category: 'single', group: ['date'],          outputType: 'number' },
  { op: 'MONTHS_FROM_TODAY',label: 'Months from Today',    category: 'single', group: ['date'],          outputType: 'number' },
  { op: 'AGE_YEARS',        label: 'Age in Years',         category: 'single', group: ['date'],          outputType: 'number' },

  // ── Cat 2: Two-column arithmetic ────────────────────────────────────────────
  { op: 'DIFF',             label: 'A − B (Difference)',   category: 'two_col', group: ['numeric'],      outputType: 'number' },
  { op: 'RATIO',            label: 'A ÷ B (Ratio)',        category: 'two_col', group: ['numeric'],      outputType: 'number' },
  { op: 'ADD',              label: 'A + B (Sum)',          category: 'two_col', group: ['numeric'],      outputType: 'number' },
  { op: 'MULTIPLY',         label: 'A × B (Product)',      category: 'two_col', group: ['numeric'],      outputType: 'number' },
  { op: 'MAX_OF',           label: 'Greater of A, B',      category: 'two_col', group: ['numeric'],      outputType: 'number' },
  { op: 'MIN_OF',           label: 'Lesser of A, B',       category: 'two_col', group: ['numeric'],      outputType: 'number' },
  { op: 'DAYS_BETWEEN_COLS',label: 'Days Between A and B', category: 'two_col', group: ['date'],         outputType: 'number' },

  // ── Cat 3: Multi-column combine ─────────────────────────────────────────────
  { op: 'CONCAT',           label: 'Concatenate Columns',  category: 'multi_col', group: ['any'],        outputType: 'string' },
  { op: 'COALESCE',         label: 'First Non-null Value', category: 'multi_col', group: ['any'],        outputType: 'string' },

  // ── Cat 4: Conditional IF ───────────────────────────────────────────────────
  { op: 'IF',               label: 'If / Then / Else',     category: 'conditional', group: ['any'],      outputType: 'string' },

  // ── Cat 5: Multi-branch CASE ────────────────────────────────────────────────
  { op: 'CASE',             label: 'Case When',            category: 'case',    group: ['any'],          outputType: 'string' },

  // ── Cat 6: Window / group aggregate ────────────────────────────────────────
  { op: 'GROUP_SUM',        label: 'Sum by Group',         category: 'window',  group: ['numeric'],      outputType: 'number' },
  { op: 'GROUP_COUNT',      label: 'Count by Group',       category: 'window',  group: ['any'],          outputType: 'number' },
  { op: 'GROUP_MEAN',       label: 'Mean by Group',        category: 'window',  group: ['numeric'],      outputType: 'number' },
  { op: 'GROUP_MAX',        label: 'Max by Group',         category: 'window',  group: ['numeric'],      outputType: 'number' },
  { op: 'GROUP_MIN',        label: 'Min by Group',         category: 'window',  group: ['numeric'],      outputType: 'number' },
  { op: 'GROUP_RANK',       label: 'Rank within Group',    category: 'window',  group: ['numeric'],      outputType: 'number' },
];

const FORMULA_CATEGORY_LABELS: Record<FormulaCategory, string> = {
  single:      'Single-column transform',
  two_col:     'Two-column arithmetic',
  multi_col:   'Multi-column combine',
  conditional: 'Conditional (IF/THEN/ELSE)',
  case:        'Multi-branch (CASE WHEN)',
  window:      'Window / Group aggregate',
};

// Condition operators for IF / CASE
const COND_OPS = [
  { value: 'eq',         label: '= (equals)' },
  { value: 'neq',        label: '≠ (not equals)' },
  { value: 'gt',         label: '> (greater than)' },
  { value: 'gte',        label: '≥ (greater or equal)' },
  { value: 'lt',         label: '< (less than)' },
  { value: 'lte',        label: '≤ (less or equal)' },
  { value: 'contains',   label: 'contains' },
  { value: 'is_null',    label: 'is null' },
  { value: 'is_not_null',label: 'is not null' },
];

function colSemanticGroup(semanticType: string): FormulaGroup {
  const t = (semanticType || '').toLowerCase();
  if (['number','numeric','integer','float','currency','continuous','int','decimal','amount','quantity'].some(s => t.includes(s))) return 'numeric';
  if (['date','datetime','timestamp','time'].some(s => t.includes(s))) return 'date';
  return 'text';
}

function formulaLabel(op: string): string {
  return FORMULA_CATALOGUE.find(f => f.op === op)?.label ?? op;
}

function parseFormulaExpression(expression: string): { op: string; col: string; param: string } {
  if (!expression) return { op: '', col: '', param: '' };
  const trimmed = expression.trim();
  if (trimmed.startsWith('{')) {
    try {
      const obj = JSON.parse(trimmed);
      return { op: obj.op ?? '', col: obj.val_col ?? obj.cond_col ?? '', param: '' };
    } catch { return { op: '', col: '', param: '' }; }
  }
  const parts = trimmed.split(':', 3);
  return { op: parts[0] ?? '', col: parts[1] ?? '', param: parts[2] ?? '' };
}

// Build serialized expression from draft fields
function buildFormulaExpression(draft: RuleDraft): string {
  const cat = FORMULA_CATALOGUE.find(f => f.op === draft.formula_op)?.category ?? 'single';
  if (cat === 'single' || cat === 'two_col') {
    const param = cat === 'two_col' ? draft.formula_col_b : draft.formula_param;
    return param ? `${draft.formula_op}:${draft.formula_input_col}:${param}` : `${draft.formula_op}:${draft.formula_input_col}`;
  }
  if (cat === 'multi_col') {
    return JSON.stringify({ op: draft.formula_op, cols: draft.formula_multi_cols, sep: draft.formula_param || ' ' });
  }
  // An IF is a CASE with one branch, and both are now written through the same
  // form, so both are stored the same way. A single branch still reads as an IF
  // wherever it is shown; nothing is lost by not having a separate shape for it.
  if (cat === 'conditional' || cat === 'case') {
    return JSON.stringify({
      op: 'CASE',
      whens: draft.formula_case_whens.map(w => ({
        ...w,
        // The column and test are chosen once for the whole rule; each branch
        // carries them so what is stored stays self-contained.
        col: draft.formula_input_col || w.col,
        op: draft.formula_cond_op || w.op,
      })),
      else_col: draft.formula_else_col,
      else_val: draft.formula_else_val,
    });
  }
  if (cat === 'window') {
    return JSON.stringify({ op: draft.formula_op, val_col: draft.formula_input_col, group_cols: draft.formula_multi_cols });
  }
  return '';
}

type RuleType = 'clean' | 'formula' | 'lookup';
type JoinType = 'inner' | 'left' | 'anti';

const TYPE_LABELS: Record<RuleType, string> = { clean: 'Cleaning', formula: 'Formula', lookup: 'Lookup' };
const TYPE_COLORS: Record<RuleType, string> = { clean: '#4f46e5', formula: '#0891b2', lookup: '#059669' };

// ── Reusable column pickers ──────────────────────────────────────────────────
const ColSelect: React.FC<{
  label: string; required?: boolean; value: string;
  onChange: (v: string) => void; disabled?: boolean; columns: StandardColumn[];
}> = ({ label, required, value, onChange, disabled, columns }) => (
  <FormControl.Select label={label} required={required} value={value}
    onChange={e => onChange(e.target.value)} disabled={disabled}>
    <option value="">— select column —</option>
    {columns.map(c => <option key={c.name} value={c.name}>{c.display_name || c.name}</option>)}
  </FormControl.Select>
);

const MultiColSelect: React.FC<{
  label: string; value: string[]; onChange: (v: string[]) => void;
  disabled?: boolean; columns: StandardColumn[]; emptyMsg?: string;
}> = ({ label, value, onChange, disabled, columns, emptyMsg }) => (
  <div>
    <label style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-heading)', display: 'block', marginBottom: 6 }}>{label}</label>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', padding: '8px 10px', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 8, background: 'var(--bg-card,#fff)', minHeight: 38 }}>
      {columns.length === 0
        ? <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{emptyMsg ?? 'No columns available.'}</span>
        : columns.map(c => (
          <label key={c.name} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, cursor: disabled ? 'not-allowed' : 'pointer' }}>
            <input type="checkbox" checked={value.includes(c.name)} disabled={disabled}
              onChange={e => e.target.checked ? onChange([...value, c.name]) : onChange(value.filter(v => v !== c.name))} />
            {c.display_name || c.name}
          </label>
        ))}
    </div>
  </div>
);

// ── Handle ───────────────────────────────────────────────────────────────────
export interface CmSourceRuleEditorHandle {
  openAdd: (type?: RuleType) => void;
  /**
   * Open an existing rule in the form, filled in. Needed by the Preprocessing
   * list, which shows every rule across all data sets and has to be able to
   * open any of them; without this a click could only ever offer a blank form
   * of the same kind, which looks like editing and is not.
   */
  openEditById: (ruleId: number) => boolean;
}

interface Props {
  source: DataSource;
  columns?: StandardColumn[];
  readOnly?: boolean;
  onChanged?: () => void;
}

// ── CASE WHEN row ────────────────────────────────────────────────────────────
interface CaseWhen { col: string; op: string; val: string; result_col: string; result_val: string; }

// ── Draft ────────────────────────────────────────────────────────────────────
interface RuleDraft {
  id?: number; rule_type: RuleType; step_order: number;
  operation: CleanOp; params: Record<string, string>;
  // formula — primary fields (all categories)
  formula_op: string;
  formula_input_col: string;   // primary input / condition col (single, two_col, conditional, window)
  formula_param: string;       // single-col param OR concat separator
  // two_col
  formula_col_b: string;
  // multi_col (CONCAT, COALESCE) + window group_cols
  formula_multi_cols: string[];
  // conditional (IF)
  formula_cond_op: string;
  formula_cond_val: string;
  formula_then_col: string; formula_then_val: string;
  formula_else_col: string; formula_else_val: string;
  // case (CASE WHEN)
  formula_case_whens: CaseWhen[];
  output_column: string; output_type: string;
  ref_source_id: string; join_type: JoinType;
  join_keys: { lhs: string; rhs: string }[]; lookup_columns: string[];
}

const BLANK_WHEN = (): CaseWhen => ({ col: '', op: 'eq', val: '', result_col: '', result_val: '' });

const mkDraft = (type: RuleType, order: number): RuleDraft => ({
  rule_type: type, step_order: order,
  operation: 'fill_nulls', params: { ...DEFAULT_CLEAN_PARAMS.fill_nulls },
  formula_op: '', formula_input_col: '', formula_param: '',
  formula_col_b: '',
  formula_multi_cols: [],
  formula_cond_op: 'eq', formula_cond_val: '',
  formula_then_col: '', formula_then_val: '',
  formula_else_col: '', formula_else_val: '',
  formula_case_whens: [BLANK_WHEN()],
  output_column: '', output_type: 'number',
  ref_source_id: '', join_type: 'left',
  join_keys: [{ lhs: '', rhs: '' }], lookup_columns: [],
});

const ruleToDraft = (r: SourceRule): RuleDraft => {
  const expr = r.expression ?? '';
  const isJson = expr.trim().startsWith('{');
  let parsed: any = {};
  if (isJson) { try { parsed = JSON.parse(expr); } catch { parsed = {}; } }
  const legacyParts = isJson ? { op: parsed.op ?? '', col: parsed.val_col ?? parsed.cond_col ?? '', param: '' }
    : (() => { const p = expr.split(':', 3); return { op: p[0] ?? '', col: p[1] ?? '', param: p[2] ?? '' }; })();
  const def = FORMULA_CATALOGUE.find(f => f.op === legacyParts.op);
  const cat = def?.category ?? 'single';
  return {
    id: r.id, rule_type: r.rule_type, step_order: r.step_order,
    operation: (r.operation as CleanOp) ?? 'fill_nulls',
    params: (r.params as Record<string, string>) ?? {},
    formula_op: legacyParts.op,
    formula_input_col: legacyParts.col,
    formula_param: cat === 'single' ? legacyParts.param : (parsed.sep ?? ''),
    formula_col_b: cat === 'two_col' ? legacyParts.param : '',
    formula_multi_cols: (cat === 'multi_col' || cat === 'window') ? (parsed.cols ?? parsed.group_cols ?? []) : [],
    formula_cond_op: parsed.cond_op ?? 'eq',
    formula_cond_val: parsed.cond_val ?? '',
    formula_then_col: parsed.then_col ?? '', formula_then_val: parsed.then_val ?? '',
    formula_else_col: parsed.else_col ?? '', formula_else_val: parsed.else_val ?? '',
    formula_case_whens: (parsed.whens ?? [BLANK_WHEN()]) as CaseWhen[],
    output_column: r.output_column ?? '', output_type: r.output_type ?? 'number',
    ref_source_id: r.ref_source_id != null ? String(r.ref_source_id) : '',
    join_type: (r.join_type as JoinType) ?? 'left',
    join_keys: (r.join_keys ?? []).map((k: any) => ({ lhs: k.left_col ?? '', rhs: k.right_col ?? '' })),
    lookup_columns: (r.selected_columns ?? []) as string[],
  };
};

// ── Main ─────────────────────────────────────────────────────────────────────
const CmSourceRuleEditor = forwardRef<CmSourceRuleEditorHandle, Props>(
  ({ source, columns = [], readOnly = false, onChanged }, ref) => {
    const [rules, setRules] = useState<SourceRule[]>([]);
    const [loading, setLoading] = useState(false);
    const [allSources, setAllSources] = useState<DataSource[]>([]);
    const [refColumns, setRefColumns] = useState<StandardColumn[]>([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [draft, setDraft] = useState<RuleDraft>(mkDraft('clean', 0));
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    /**
     * Which kind of formula is being chosen from.
     *
     * Fifty-eight formulas in one list is too many to read, so the choice is
     * made in two steps: the kind first, then the formulas of that kind only.
     * This holds the first step. It is set from the rule being edited so that
     * opening an existing rule shows the right list straight away.
     */
    const [pickedCat, setPickedCat] = useState<FormulaCategory | ''>('');
    const load = useCallback(async () => {
      setLoading(true);
      try { setRules(await FileAnalyticsAPI.listSourceRules(Number(source.id))); }
      catch { /* non-fatal */ }
      setLoading(false);
    }, [source.id]);

    useEffect(() => { load(); }, [load]);

    const loadSources = async () => {
      try {
        const list = await FileAnalyticsAPI.listSources();
        setAllSources(list.filter(s => String(s.id) !== String(source.id)));
      } catch { /* non-fatal */ }
    };

    // Load ref source columns when ref_source_id changes
    const loadRefColumns = useCallback(async (refId: string) => {
      if (!refId) { setRefColumns([]); return; }
      const found = allSources.find(s => String(s.id) === refId);
      setRefColumns(found?.standard_schema ?? []);
    }, [allSources]);

    useEffect(() => { loadRefColumns(draft.ref_source_id); }, [draft.ref_source_id, loadRefColumns]);

    const openAdd = useCallback((type: RuleType = 'clean') => {
      const nextOrder = rules.length > 0 ? Math.max(...rules.map(r => r.step_order)) + 1 : 0;
      setDraft(mkDraft(type, nextOrder));
      setRefColumns([]);
      setPickedCat('');          // a new rule starts with neither step chosen
      if (type === 'lookup') loadSources();
      setModalOpen(true);
    }, [rules]); // eslint-disable-line react-hooks/exhaustive-deps

    const openEdit = (rule: SourceRule) => {
      const d = ruleToDraft(rule);
      setDraft(d);
      setRefColumns([]);
      // The first step is set from the rule itself, so opening an existing rule
      // shows its own kind and the formula list it belongs to. Without this the
      // second dropdown would be empty and the rule would look unset.
      setPickedCat(FORMULA_CATALOGUE.find(f => f.op === d.formula_op)?.category ?? '');
      if (rule.rule_type === 'lookup') {
        loadSources().then(() => {});
      }
      setModalOpen(true);
    };

    useImperativeHandle(ref, () => ({
      openAdd,
      // Returns false when the rule is not among those loaded — the caller then
      // knows the form was not opened, rather than being left with nothing
      // happening and no reason why.
      openEditById: (ruleId: number) => {
        const rule = rules.find(r => Number(r.id) === Number(ruleId));
        if (!rule) return false;
        openEdit(rule);
        return true;
      },
    }), [openAdd, rules]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSave = async () => {
      setSaving(true);
      try {
        const formulaExpression = buildFormulaExpression(draft);
        const payload: SourceRuleCreate | SourceRuleUpdate = {
          rule_type: draft.rule_type,
          step_order: draft.step_order,
          ...(draft.rule_type === 'clean' ? { operation: draft.operation, params: draft.params } : {}),
          ...(draft.rule_type === 'formula' ? {
            expression: formulaExpression,
            output_column: draft.output_column,
            output_type: draft.output_type,
          } : {}),
          ...(draft.rule_type === 'lookup' ? {
            ref_source_id: Number(draft.ref_source_id),
            join_type: 'left' as const,
            join_keys: draft.join_keys[0]?.lhs && draft.join_keys[0]?.rhs
              ? [{ left_col: draft.join_keys[0].lhs, right_col: draft.join_keys[0].rhs }]
              : [],
            selected_columns: draft.lookup_columns.length > 0 ? draft.lookup_columns : undefined,
          } : {}),
        };
        if (draft.id) {
          const updated = await FileAnalyticsAPI.updateSourceRule(Number(source.id), draft.id, payload as SourceRuleUpdate);
          setModalOpen(false);
          await load();
          onChanged?.();
          const warn = (updated as any)?._edit_warning;
          if (warn) setToast({ type: 'warning', message: warn });
        } else {
          await FileAnalyticsAPI.createSourceRule(Number(source.id), payload as SourceRuleCreate);
          setModalOpen(false);
          await load();
          onChanged?.();
        }
      } catch (err: any) {
        setToast({ type: 'error', message: err?.response?.data?.detail ?? 'Save failed' });
      }
      setSaving(false);
    };

    const handleDelete = async (ruleId: number) => {
      try {
        await FileAnalyticsAPI.deleteSourceRule(Number(source.id), ruleId);
        await load();
        onChanged?.();
      } catch (err: any) {
        setToast({ type: 'error', message: err?.response?.data?.detail ?? 'Delete failed' });
      }
    };

    const setParam = (key: string, value: string) =>
      setDraft(d => ({ ...d, params: { ...d.params, [key]: value } }));

    const formulaCat = FORMULA_CATALOGUE.find(f => f.op === draft.formula_op)?.category ?? 'single';
    const canSave =
      draft.rule_type === 'clean' ? !!draft.operation
      : draft.rule_type === 'formula' ? (() => {
          if (!draft.formula_op || !draft.output_column.trim()) return false;
          if (formulaCat === 'single')      return !!draft.formula_input_col;
          if (formulaCat === 'two_col')     return !!draft.formula_input_col && !!draft.formula_col_b;
          if (formulaCat === 'multi_col')   return draft.formula_multi_cols.length >= 2;
          // Both are the same form now: a column, a test, and at least one
          // branch. The column is asked once at the top rather than per branch,
          // so it is checked there.
          if (formulaCat === 'conditional' || formulaCat === 'case') {
            return !!draft.formula_input_col
                && !!draft.formula_cond_op
                && draft.formula_case_whens.length > 0;
          }
          if (formulaCat === 'window')      return !!draft.formula_input_col;
          return true;
        })()
      : !!draft.ref_source_id && !!(draft.join_keys[0]?.lhs) && !!(draft.join_keys[0]?.rhs);

    return (
      <>
        <Toast toast={toast} onClose={() => setToast(null)} />

        {/* Rule list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 0' }}>
          {loading && <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '8px 0' }}>Loading…</div>}
          {!loading && rules.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '8px 0' }}>
              No rules yet.{!readOnly && ' Click + to add a cleaning, formula, or lookup rule.'}
            </div>
          )}
          {rules.map((rule, idx) => (
            <div
              key={rule.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: 'var(--bg-card,#fff)',
                border: '1px solid var(--border-color,#e5e7eb)',
                borderRadius: 8, padding: '6px 12px',
              }}
            >
              {/* Global sequence number (immutable — assigned at creation) */}
              <span
                title={rule.global_seq != null ? `Global execution position: ${rule.global_seq} (immutable — delete and recreate to change)` : 'Sequence not yet assigned'}
                style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', minWidth: 30, textAlign: 'center', flexShrink: 0 }}
              >
                {rule.global_seq ?? '—'}
              </span>

              {/* Clickable body — opens edit modal */}
              <div
                onClick={() => !readOnly && openEdit(rule)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, flex: 1,
                  overflow: 'hidden', cursor: readOnly ? 'default' : 'pointer',
                }}
              >
                <span style={{
                  fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                  background: `${TYPE_COLORS[rule.rule_type]}20`, color: TYPE_COLORS[rule.rule_type], flexShrink: 0,
                }}>{TYPE_LABELS[rule.rule_type]}</span>
                <span style={{ fontSize: 13, flex: 1, color: 'var(--text-heading)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {rule.rule_type === 'clean' && (CLEAN_OP_LABELS[rule.operation as CleanOp] ?? rule.operation)}
                  {rule.rule_type === 'formula' && (() => {
                    const { op, col } = parseFormulaExpression(rule.expression ?? '');
                    const fDef = FORMULA_CATALOGUE.find(f => f.op === op);
                    const label = fDef?.label ?? op;
                    const catLabel = fDef ? FORMULA_CATEGORY_LABELS[fDef.category] : '';
                    const suffix = col ? `(${col})` : '';
                    return rule.output_column
                      ? `${rule.output_column} = ${label}${suffix}`
                      : catLabel || rule.expression;
                  })()}
                  {rule.rule_type === 'lookup' && (() => {
                    const src = rule.ref_source_name ?? `Source #${rule.ref_source_id}`;
                    const cols = rule.selected_columns ?? [];
                    const colSuffix = cols.length > 0 ? ` (${cols.length} col${cols.length > 1 ? 's' : ''})` : ' (all cols)';
                    return `Lookup ← ${src}${colSuffix}`;
                  })()}
                </span>
              </div>

            </div>
          ))}
        </div>

        {/* Rule editor modal */}
        {/* Wider, and a fixed height. The form differs a great deal between a
            cleaning rule and a multi-branch condition, and letting the modal
            size itself to each meant it jumped every time something was chosen.
            A constant height with the body scrolling keeps the header, the
            footer and the Add button exactly where they were. */}
        <Modal isOpen={modalOpen} onClose={saving ? () => {} : () => setModalOpen(false)} size="xl">
          <Modal.Header>{`${draft.id ? 'Edit' : 'Add'} ${TYPE_LABELS[draft.rule_type]} Rule — “${source.name}”`}</Modal.Header>
          <Modal.Body>
            {/* A constant height, whatever the form holds, so the modal does not
                resize as choices are made and the buttons stay where they were. */}
            <div style={{
              display: 'flex', flexDirection: 'column', gap: 14,
              height: '60vh', minHeight: 400, overflowY: 'auto', paddingRight: 4,
            }}>

              {/* ── Clean ─────────────────────────────────────────── */}
              {draft.rule_type === 'clean' && (
                <>
                  <FormControl.Select label="Operation" required value={draft.operation} disabled={saving}
                    onChange={e => { const op = e.target.value as CleanOp; setDraft(d => ({ ...d, operation: op, params: { ...DEFAULT_CLEAN_PARAMS[op] } })); }}>
                    {(Object.keys(CLEAN_OP_LABELS) as CleanOp[]).map(op => (
                      <option key={op} value={op}>{CLEAN_OP_LABELS[op]}</option>
                    ))}
                  </FormControl.Select>

                  {draft.operation === 'fill_nulls' && (<>
                    <ColSelect label="Column" required columns={columns} value={draft.params.column ?? ''} onChange={v => setParam('column', v)} disabled={saving} />
                    <FormControl.Input label="Fill value" value={draft.params.fill_with ?? '0'} onChange={e => setParam('fill_with', e.target.value)} disabled={saving} />
                  </>)}
                  {draft.operation === 'trim_whitespace' && (
                    <ColSelect label="Column" required columns={columns} value={draft.params.column ?? ''} onChange={v => setParam('column', v)} disabled={saving} />
                  )}
                  {draft.operation === 'standardize_dates' && (<>
                    <ColSelect label="Column" required columns={columns} value={draft.params.column ?? ''} onChange={v => setParam('column', v)} disabled={saving} />
                    <FormControl.Input label="Target format" value={draft.params.target_format ?? '%Y-%m-%d'} onChange={e => setParam('target_format', e.target.value)} disabled={saving} />
                  </>)}
                  {draft.operation === 'remove_duplicates' && (<>
                    <MultiColSelect
                      label="Columns to deduplicate on" columns={columns}
                      value={(draft.params.columns ?? '').split(',').map(s => s.trim()).filter(Boolean)}
                      onChange={cols => setParam('columns', cols.join(', '))}
                      disabled={saving}
                      emptyMsg="No columns defined — add columns in Step II first."
                    />
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '4px 0' }}>
                      Legacy operation — use "Flag Duplicates" for new rules (non-destructive).
                    </div>
                  </>)}
                  {draft.operation === 'find_replace' && (<>
                    <ColSelect label="Column" required columns={columns} value={draft.params.column ?? ''} onChange={v => setParam('column', v)} disabled={saving} />
                    <FormControl.Input label="Find" required value={draft.params.find ?? ''} onChange={e => setParam('find', e.target.value)} disabled={saving} />
                    <FormControl.Input label="Replace with" value={draft.params.replace ?? ''} onChange={e => setParam('replace', e.target.value)} disabled={saving} />
                  </>)}
                  {draft.operation === 'cast_type' && (<>
                    <ColSelect label="Column" required columns={columns} value={draft.params.column ?? ''} onChange={v => setParam('column', v)} disabled={saving} />
                    <FormControl.Select label="Cast to type" required value={draft.params.to_type ?? 'numeric'} onChange={e => setParam('to_type', e.target.value)} disabled={saving}>
                      {['numeric', 'integer', 'string', 'date', 'boolean'].map(t => <option key={t} value={t}>{t}</option>)}
                    </FormControl.Select>
                  </>)}

                  {/* ── Null-handling ─────────────────────────────── */}
                  {(draft.operation === 'fill_forward' || draft.operation === 'fill_backward' ||
                    draft.operation === 'fill_mean' || draft.operation === 'fill_median' ||
                    draft.operation === 'fill_mode') && (
                    <ColSelect label="Column" required columns={columns} value={draft.params.column ?? ''} onChange={v => setParam('column', v)} disabled={saving} />
                  )}
                  {draft.operation === 'flag_nulls' && (<>
                    <ColSelect label="Column" required columns={columns} value={draft.params.column ?? ''} onChange={v => setParam('column', v)} disabled={saving} />
                    {draft.params.column && (
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '4px 0' }}>
                        Output column: <strong>is_null_{draft.params.column}</strong>
                      </div>
                    )}
                  </>)}

                  {/* ── String/text ───────────────────────────────── */}
                  {(draft.operation === 'normalize_whitespace' || draft.operation === 'to_uppercase' ||
                    draft.operation === 'to_lowercase' || draft.operation === 'to_titlecase' ||
                    draft.operation === 'parse_boolean' || draft.operation === 'standardize_number_format' ||
                    draft.operation === 'normalize_scale') && (
                    <ColSelect label="Column" required columns={columns} value={draft.params.column ?? ''} onChange={v => setParam('column', v)} disabled={saving} />
                  )}
                  {draft.operation === 'remove_special_chars' && (<>
                    <ColSelect label="Column" required columns={columns} value={draft.params.column ?? ''} onChange={v => setParam('column', v)} disabled={saving} />
                    <FormControl.Input label="Keep pattern (regex)" value={draft.params.keep_pattern ?? '[^a-zA-Z0-9 ]'} onChange={e => setParam('keep_pattern', e.target.value)} disabled={saving} placeholder="[^a-zA-Z0-9 ]" />
                  </>)}
                  {draft.operation === 'extract_regex' && (<>
                    <ColSelect label="Column" required columns={columns} value={draft.params.column ?? ''} onChange={v => setParam('column', v)} disabled={saving} />
                    <FormControl.Input label="Pattern (regex)" required value={draft.params.pattern ?? ''} onChange={e => setParam('pattern', e.target.value)} disabled={saving} placeholder="e.g. (\d+)" />
                    <FormControl.Input label="Output column name" required value={draft.params.output_column ?? ''} onChange={e => setParam('output_column', e.target.value)} disabled={saving} placeholder="e.g. extracted_number" />
                  </>)}
                  {draft.operation === 'pad_left' && (<>
                    <ColSelect label="Column" required columns={columns} value={draft.params.column ?? ''} onChange={v => setParam('column', v)} disabled={saving} />
                    <FormControl.Input label="Width" type="number" value={draft.params.width ?? '10'} onChange={e => setParam('width', e.target.value)} disabled={saving} />
                    <FormControl.Input label="Pad character" value={draft.params.pad_char ?? '0'} onChange={e => setParam('pad_char', e.target.value)} disabled={saving} placeholder="0" />
                  </>)}

                  {/* ── Dedup ─────────────────────────────────────── */}
                  {draft.operation === 'flag_duplicates' && (
                    <MultiColSelect
                      label="Columns to check for duplicates" columns={columns}
                      value={(draft.params.columns ?? '').split(',').map(s => s.trim()).filter(Boolean)}
                      onChange={cols => setParam('columns', cols.join(', '))}
                      disabled={saving}
                      emptyMsg="No columns defined — add columns in Step II first."
                    />
                  )}

                  {/* ── Range/outlier ─────────────────────────────── */}
                  {draft.operation === 'clamp_range' && (<>
                    <ColSelect label="Column" required columns={columns} value={draft.params.column ?? ''} onChange={v => setParam('column', v)} disabled={saving} />
                    <FormControl.Input label="Min value" type="number" value={draft.params.min_val ?? ''} onChange={e => setParam('min_val', e.target.value)} disabled={saving} />
                    <FormControl.Input label="Max value" type="number" value={draft.params.max_val ?? ''} onChange={e => setParam('max_val', e.target.value)} disabled={saving} />
                  </>)}
                  {draft.operation === 'flag_outliers' && (<>
                    <ColSelect label="Column" required columns={columns} value={draft.params.column ?? ''} onChange={v => setParam('column', v)} disabled={saving} />
                    <FormControl.Input label="Z-score threshold" type="number" value={draft.params.z_threshold ?? '3'} onChange={e => setParam('z_threshold', e.target.value)} disabled={saving} />
                  </>)}

                  {/* ── Split/combine ─────────────────────────────── */}
                  {draft.operation === 'split_column' && (<>
                    <ColSelect label="Column" required columns={columns} value={draft.params.column ?? ''} onChange={v => setParam('column', v)} disabled={saving} />
                    <FormControl.Input label="Delimiter" value={draft.params.delimiter ?? ','} onChange={e => setParam('delimiter', e.target.value)} disabled={saving} placeholder="," />
                    <FormControl.Input label="Left output column" value={draft.params.left_output ?? ''} onChange={e => setParam('left_output', e.target.value)} disabled={saving} placeholder="e.g. col_left" />
                    <FormControl.Input label="Right output column" value={draft.params.right_output ?? ''} onChange={e => setParam('right_output', e.target.value)} disabled={saving} placeholder="e.g. col_right" />
                  </>)}
                  {draft.operation === 'concat_columns' && (<>
                    <ColSelect label="First column (A)" required columns={columns} value={draft.params.col_a ?? ''} onChange={v => setParam('col_a', v)} disabled={saving} />
                    <ColSelect label="Second column (B)" required columns={columns} value={draft.params.col_b ?? ''} onChange={v => setParam('col_b', v)} disabled={saving} />
                    <FormControl.Input label="Separator" value={draft.params.separator ?? ' '} onChange={e => setParam('separator', e.target.value)} disabled={saving} placeholder=" " />
                    <FormControl.Input label="Output column name" required value={draft.params.output_column ?? ''} onChange={e => setParam('output_column', e.target.value)} disabled={saving} placeholder="e.g. full_name" />
                  </>)}
                </>
              )}

              {/* ── Formula ───────────────────────────────────────── */}
              {draft.rule_type === 'formula' && (() => {
                const def = FORMULA_CATALOGUE.find(f => f.op === draft.formula_op);
                const cat = def?.category ?? null;

                const handleOpChange = (op: string) => {
                  const newDef = FORMULA_CATALOGUE.find(f => f.op === op);
                  setDraft(d => ({
                    ...d,
                    formula_op: op,
                    formula_param: newDef?.paramDefault ?? '',
                    formula_col_b: '',
                    formula_multi_cols: [],
                    formula_cond_op: 'eq', formula_cond_val: '',
                    formula_then_col: '', formula_then_val: '',
                    formula_else_col: '', formula_else_val: '',
                    formula_case_whens: [BLANK_WHEN()],
                    output_type: newDef?.outputType ?? 'string',
                    output_column: d.output_column,
                  }));
                };

                const setWhen = (idx: number, field: keyof CaseWhen, val: string) =>
                  setDraft(d => {
                    const whens = d.formula_case_whens.map((w, i) => i === idx ? { ...w, [field]: val } : w);
                    return { ...d, formula_case_whens: whens };
                  });

                // Derived: filtered formula list
                const cats = Array.from(new Set(FORMULA_CATALOGUE.map(f => f.category))) as FormulaCategory[];

                return (
                  <>
                    {/*
                      One dropdown, grouped by kind of formula.

                      This was three things — a row of category buttons, a search
                      box, and a scrolling list — and the state of each was hard
                      to read: choosing a formula narrowed the list to one entry,
                      which made the buttons look as though nothing was selected.
                      A dropdown holds its own selection visibly, needs no
                      explaining, and the grouping keeps all fifty-eight
                      formulas findable. Typing in an open dropdown jumps to a
                      match, so nothing is lost by removing the search box.
                    */}
                    {/*
                      The formula is chosen in two steps, because fifty-eight of
                      them in one list is more than anyone can read. The kind
                      first, then only the formulas of that kind — usually a
                      handful.
                    */}
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <div style={{ flex: '1 1 210px', minWidth: 190 }}>
                        <label style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-heading)', display: 'block', marginBottom: 6 }}>
                          Kind of formula <span style={{ color: 'var(--red-600,#dc2626)' }}>*</span>
                        </label>
                        <select
                          className="fi fi--input"
                          value={pickedCat}
                          disabled={saving}
                          // Changing the kind clears the formula: the one chosen
                          // before belongs to a different list, and leaving it
                          // set would show fields that no longer match.
                          onChange={e => {
                            setPickedCat(e.target.value as FormulaCategory | '');
                            handleOpChange('');
                          }}
                          style={{ width: '100%' }}
                          data-testid="SEL_formula_cat"
                        >
                          <option value="">— choose —</option>
                          {cats.map(c => (
                            <option key={c} value={c}>{FORMULA_CATEGORY_LABELS[c]}</option>
                          ))}
                        </select>
                      </div>

                      <div style={{ flex: '1 1 260px', minWidth: 220 }}>
                        <label style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-heading)', display: 'block', marginBottom: 6 }}>
                          Formula <span style={{ color: 'var(--red-600,#dc2626)' }}>*</span>
                        </label>
                        <select
                          className="fi fi--input"
                          value={draft.formula_op}
                          disabled={saving || !pickedCat}
                          onChange={e => handleOpChange(e.target.value)}
                          style={{ width: '100%' }}
                          data-testid="SEL_formula_op"
                        >
                          <option value="">
                            {pickedCat ? '— choose a formula —' : '— choose a kind first —'}
                          </option>
                          {pickedCat && FORMULA_CATALOGUE
                            .filter(f => f.category === pickedCat)
                            .map(f => (
                              <option key={f.op} value={f.op}>{f.label}</option>
                            ))}
                        </select>
                      </div>
                    </div>

                    {/* ── Cat 1: Single-column ──────────────────────── */}
                    {cat === 'single' && (<>
                      <ColSelect label="Input column" required columns={columns}
                        value={draft.formula_input_col}
                        onChange={v => setDraft(d => ({ ...d, formula_input_col: v }))}
                        disabled={saving} />
                      {def?.hasParam && (
                        <FormControl.Input
                          label={def.paramLabel ?? 'Parameter'} required
                          type="number" min="0"
                          value={draft.formula_param}
                          placeholder={def.paramPlaceholder ?? def.paramDefault ?? ''}
                          onChange={e => setDraft(d => ({ ...d, formula_param: e.target.value }))}
                          disabled={saving} />
                      )}
                    </>)}

                    {/* ── Cat 2: Two-column arithmetic ──────────────── */}
                    {cat === 'two_col' && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 8, alignItems: 'end' }}>
                        <ColSelect label="Column A" required columns={columns}
                          value={draft.formula_input_col}
                          onChange={v => setDraft(d => ({ ...d, formula_input_col: v }))}
                          disabled={saving} />
                        <span style={{ fontSize: 18, color: 'var(--text-muted)', textAlign: 'center', paddingBottom: 8 }}>
                          {draft.formula_op === 'ADD' ? '+' : draft.formula_op === 'MULTIPLY' ? '×' : draft.formula_op === 'RATIO' ? '÷' : draft.formula_op === 'MAX_OF' ? 'max' : draft.formula_op === 'MIN_OF' ? 'min' : '−'}
                        </span>
                        <ColSelect label="Column B" required columns={columns}
                          value={draft.formula_col_b}
                          onChange={v => setDraft(d => ({ ...d, formula_col_b: v }))}
                          disabled={saving} />
                      </div>
                    )}

                    {/* ── Cat 3: Multi-column combine ───────────────── */}
                    {cat === 'multi_col' && (<>
                      {draft.formula_op === 'CONCAT' && (
                        <FormControl.Input label="Separator" value={draft.formula_param}
                          placeholder='e.g. space, comma, dash — default " "'
                          onChange={e => setDraft(d => ({ ...d, formula_param: e.target.value }))}
                          disabled={saving} />
                      )}
                      <MultiColSelect
                        label={draft.formula_op === 'CONCAT' ? 'Columns to concatenate (in order)' : 'Columns (first non-null wins)'}
                        columns={columns}
                        value={draft.formula_multi_cols}
                        onChange={v => setDraft(d => ({ ...d, formula_multi_cols: v }))}
                        disabled={saving}
                        emptyMsg="No columns available." />
                      {draft.formula_multi_cols.length > 0 && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          {draft.formula_op === 'CONCAT'
                            ? `Preview: ${draft.formula_multi_cols.join(` ${draft.formula_param || ' '} `)}`
                            : `Will try: ${draft.formula_multi_cols.join(' → ')} → first non-null`}
                        </div>
                      )}
                    </>)}

                    {/* ── Cat 4: Conditional IF ─────────────────────── */}
                    {/*
                      IF/THEN/ELSE and CASE WHEN are one thing: an IF is a CASE
                      with a single branch. They were two separate forms asking
                      the same questions in different words, so they are now one.

                      The column and the test are asked ONCE at the top and apply
                      to every branch. Only the value and its result change from
                      branch to branch — which is what a person is actually
                      describing: "this column, compared this way; if it is A
                      then X, if it is B then Y, otherwise Z".

                      What is stored is unchanged: each branch still carries its
                      own column and test, filled in from the two at the top.
                    */}
                    {(cat === 'conditional' || cat === 'case') && (<>
                      <div style={{ background: 'var(--bg-subtle,#f8fafc)', borderRadius: 8, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>
                          Test this column
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                          <ColSelect label="Column" required columns={columns}
                            value={draft.formula_input_col}
                            onChange={v => setDraft(d => ({
                              ...d,
                              formula_input_col: v,
                              // Every branch tests the same column.
                              formula_case_whens: d.formula_case_whens.map(w => ({ ...w, col: v })),
                            }))}
                            disabled={saving} />
                          <FormControl.Select label="Compared how" required value={draft.formula_cond_op} disabled={saving}
                            onChange={e => setDraft(d => ({
                              ...d,
                              formula_cond_op: e.target.value,
                              formula_case_whens: d.formula_case_whens.map(w => ({ ...w, op: e.target.value })),
                            }))}>
                            {COND_OPS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </FormControl.Select>
                        </div>
                      </div>

                      {draft.formula_case_whens.map((w, idx) => (
                        <div key={idx} style={{ background: 'var(--bg-subtle,#f8fafc)', borderRadius: 8, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>
                              {idx === 0 ? 'If it is…' : `Otherwise, if it is…`}
                            </span>
                            {idx > 0 && (
                              <button type="button" disabled={saving} onClick={() => setDraft(d => ({ ...d, formula_case_whens: d.formula_case_whens.filter((_, i) => i !== idx) }))}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red-600,#dc2626)', fontSize: 12 }}>Remove</button>
                            )}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                            {!['is_null','is_not_null'].includes(draft.formula_cond_op) ? (
                              <FormControl.Input label="Value" value={w.val} placeholder="e.g. Online"
                                onChange={e => setWhen(idx, 'val', e.target.value)} disabled={saving} />
                            ) : <div />}
                            <ColSelect label="Then — column" columns={columns} value={w.result_col}
                              onChange={v => setWhen(idx, 'result_col', v)} disabled={saving} />
                            <FormControl.Input label="— or a literal" value={w.result_val} placeholder="e.g. High"
                              onChange={e => setWhen(idx, 'result_val', e.target.value)} disabled={saving} />
                          </div>
                        </div>
                      ))}

                      <button type="button" disabled={saving}
                        onClick={() => setDraft(d => ({
                          ...d,
                          // A new branch inherits the column and test from the top.
                          formula_case_whens: [...d.formula_case_whens, {
                            ...BLANK_WHEN(), col: d.formula_input_col, op: d.formula_cond_op,
                          }],
                        }))}
                        style={{ background: 'none', border: '1px dashed var(--border-color,#e5e7eb)', borderRadius: 8, cursor: 'pointer', color: 'var(--primary)', fontSize: 13, padding: '6px 12px', textAlign: 'left' }}>
                        + Another value
                      </button>

                      <div style={{ background: 'var(--bg-subtle,#f8fafc)', borderRadius: 8, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: 1 }}>Otherwise</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                          <ColSelect label="Column" columns={columns} value={draft.formula_else_col}
                            onChange={v => setDraft(d => ({ ...d, formula_else_col: v, formula_else_val: v ? '' : d.formula_else_val }))}
                            disabled={saving} />
                          <FormControl.Input label="— or a literal" value={draft.formula_else_val}
                            placeholder="e.g. Unknown"
                            onChange={e => setDraft(d => ({ ...d, formula_else_val: e.target.value, formula_else_col: e.target.value ? '' : d.formula_else_col }))}
                            disabled={saving} />
                        </div>
                      </div>
                    </>)}

                    {/* ── Cat 6: Window / group aggregate ──────────── */}
                    {cat === 'window' && (<>
                      <ColSelect label="Value column" required columns={columns}
                        value={draft.formula_input_col}
                        onChange={v => setDraft(d => ({ ...d, formula_input_col: v }))}
                        disabled={saving} />
                      <MultiColSelect
                        label="Group by columns"
                        columns={columns}
                        value={draft.formula_multi_cols}
                        onChange={v => setDraft(d => ({ ...d, formula_multi_cols: v }))}
                        disabled={saving}
                        emptyMsg="No columns available." />
                      {draft.formula_multi_cols.length === 0 && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>No group columns selected — aggregate computed over the entire dataset.</div>
                      )}
                    </>)}

                    {/* Output column name — always shown once an op is selected */}
                    {draft.formula_op && (
                      <FormControl.Input label="Output column name" required value={draft.output_column}
                        onChange={e => setDraft(d => ({ ...d, output_column: e.target.value }))}
                        placeholder="e.g. total_by_dept" disabled={saving} />
                    )}

                    {!draft.formula_op && (
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '4px 0' }}>
                        Select a formula above to see the required inputs.
                      </div>
                    )}
                  </>
                );
              })()}

              {/* ── Lookup ────────────────────────────────────────── */}
              {draft.rule_type === 'lookup' && (
                <>
                  <FormControl.Select label="Reference source" required value={draft.ref_source_id} disabled={saving}
                    onChange={e => {
                      const id = e.target.value;
                      setDraft(d => ({ ...d, ref_source_id: id, join_keys: [{ lhs: '', rhs: '' }], lookup_columns: [] }));
                    }}>
                    <option value="">— Select a data source —</option>
                    {allSources.map(s => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
                  </FormControl.Select>

                  {/* Single join key — labelled with actual source names */}
                  {(() => {
                    const refName = allSources.find(s => String(s.id) === draft.ref_source_id)?.name ?? 'Reference';
                    return (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 8, alignItems: 'end' }}>
                        <div>
                          <label style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-heading)', display: 'block', marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={source.name}>{source.name}</label>
                          <select className="fi fi--select" value={draft.join_keys[0]?.lhs ?? ''} disabled={saving}
                            onChange={e => setDraft(d => ({ ...d, join_keys: [{ lhs: e.target.value, rhs: d.join_keys[0]?.rhs ?? '' }] }))}>
                            <option value="">— select column —</option>
                            {columns.map(c => <option key={c.name} value={c.name}>{c.display_name || c.name}</option>)}
                          </select>
                        </div>
                        <span style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', paddingBottom: 8 }}>=</span>
                        <div>
                          <label style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-heading)', display: 'block', marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={refName}>{refName}</label>
                          <select className="fi fi--select" value={draft.join_keys[0]?.rhs ?? ''} disabled={saving || !draft.ref_source_id}
                            onChange={e => setDraft(d => ({ ...d, join_keys: [{ lhs: d.join_keys[0]?.lhs ?? '', rhs: e.target.value }] }))}>
                            <option value="">— select column —</option>
                            {refColumns.map(c => <option key={c.name} value={c.name}>{c.display_name || c.name}</option>)}
                          </select>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Multi-select columns to pull */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <label style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-heading)' }}>Columns to pull</label>
                      <button type="button"
                        disabled={saving || !draft.ref_source_id}
                        onClick={() => setDraft(d => ({
                          ...d,
                          lookup_columns: d.lookup_columns.length === refColumns.length ? [] : refColumns.map(c => c.name),
                        }))}
                        style={{ background: 'none', border: 'none', cursor: draft.ref_source_id ? 'pointer' : 'not-allowed', color: 'var(--primary)', fontSize: 12, padding: '2px 0', opacity: draft.ref_source_id ? 1 : 0.4 }}>
                        {draft.lookup_columns.length === refColumns.length && refColumns.length > 0 ? 'Deselect all' : 'Select all'}
                      </button>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', padding: '8px 10px', border: '1px solid var(--border-color,#e5e7eb)', borderRadius: 8, background: 'var(--bg-card,#fff)', minHeight: 38 }}>
                      {!draft.ref_source_id ? (
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Select a reference source first.</span>
                      ) : refColumns.length === 0 ? (
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>No columns defined on that source.</span>
                      ) : refColumns.map(c => (
                        <label key={c.name} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, cursor: saving ? 'not-allowed' : 'pointer' }}>
                          <input type="checkbox" disabled={saving}
                            checked={draft.lookup_columns.includes(c.name)}
                            onChange={e => setDraft(d => ({
                              ...d,
                              lookup_columns: e.target.checked
                                ? [...d.lookup_columns, c.name]
                                : d.lookup_columns.filter(n => n !== c.name),
                            }))} />
                          {c.display_name || c.name}
                        </label>
                      ))}
                    </div>
                    {draft.lookup_columns.length === 0 && draft.ref_source_id && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>No columns selected — all columns will be pulled.</div>
                    )}
                  </div>
                </>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            {draft.id && (
              <Button variant="outline" onClick={() => { setModalOpen(false); handleDelete(draft.id!); }} disabled={saving}
                style={{ marginRight: 'auto', color: 'var(--red-600,#dc2626)', borderColor: 'var(--red-600,#dc2626)' }}>
                Delete
              </Button>
            )}
            <Button variant="fill" onClick={handleSave} disabled={saving || !canSave}>
              {saving ? 'Saving…' : draft.id ? 'Update' : 'Add Rule'}
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  }
);

CmSourceRuleEditor.displayName = 'CmSourceRuleEditor';
export default CmSourceRuleEditor;
